import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import useSWR from "swr";

import { useMediaQuery } from "../hooks/useMediaQuery";
import { useModalDismiss } from "../hooks/useModalDismiss";
import { api, getImpersonationUserId, type NotificationEventRow } from "../services/api";
import {
  notificationExcerpt,
  notificationFullText,
  notificationHref,
  notificationMeta,
  notificationTime,
  notificationTitle,
} from "../utils/notifications";

import { Icon } from "./Icon";

/** Matches the sheet rules in design-system.css. 560px = --iq-bp-sm. */
const SHEET_QUERY = "(max-width: 560px)";

function countLabel(count: number): string {
  return count > 99 ? "99+" : String(count);
}

function isClamped(node: HTMLElement): boolean {
  // More than 1px ignores sub-pixel rounding that is not real clipping.
  return node.scrollHeight - node.clientHeight > 1;
}

/**
 * In-portal notification bell, for every signed-in user since #191 (it was
 * staff only). The caller passes `enabled` (the /api/me load) and `isStaff`,
 * which picks each row's View destination.
 *
 * From 561px up the panel is a popover under the bell. At 560px and below
 * it is a bottom sheet (spec 03).
 */
export function NotificationBell({
  enabled,
  isStaff = false,
}: {
  enabled: boolean;
  isStaff?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [markingAll, setMarkingAll] = useState(false);
  const [markAllError, setMarkAllError] = useState(false);
  const [markedAll, setMarkedAll] = useState(false);
  const markingAllRef = useRef(false);
  const markAllButtonRef = useRef<HTMLButtonElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const sheetWasOpen = useRef(false);
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const headingId = `${baseId}-heading`;
  const isSheet = useMediaQuery(SHEET_QUERY);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);

  const { data: unread, mutate: mutateUnread } = useSWR(
    enabled ? "/api/notifications/unread-count" : null,
    () => api.notifications.unreadCount(),
    { refreshInterval: 60_000 },
  );
  const {
    data: page,
    error: listError,
    mutate: mutateList,
  } = useSWR(
    enabled && open ? "/api/notifications?bell=1" : null,
    () => api.notifications.list({ page: 1, page_size: 8 }),
  );

  function close() {
    setOpen(false);
  }

  const sheetRef = useModalDismiss<HTMLDivElement>(
    close,
    enabled && open && isSheet,
  );

  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  useEffect(() => {
    if (pathnameRef.current === pathname) return;
    pathnameRef.current = pathname;
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) {
      setExpanded({});
      setMarkAllError(false);
      setMarkedAll(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || isSheet) return;
    const wrap = wrapRef.current;
    if (!wrap) return;

    function onPointerDown(event: PointerEvent) {
      if (!wrap?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      bellRef.current?.focus();
    }
    function onFocusOut(event: FocusEvent) {
      // Tabbing to a control outside the bell closes the panel. A null
      // relatedTarget (focus moving to the browser chrome, or jsdom's
      // programmatic focus) is not a tab-out, so leave the panel open.
      const next = event.relatedTarget;
      const root = wrapRef.current;
      if (!root) return;
      if (next instanceof Node && !root.contains(next)) setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    wrap.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      wrap.removeEventListener("focusout", onFocusOut);
    };
  }, [open, isSheet]);

  // A tap on iOS does not focus the bell, so the sheet's focus restore can
  // land on the body. After the sheet closes, put focus back on the bell.
  // This runs after useModalDismiss's own restore.
  useEffect(() => {
    const sheetOpen = open && isSheet;
    const wasOpen = sheetWasOpen.current;
    sheetWasOpen.current = sheetOpen;
    if (wasOpen && !sheetOpen) bellRef.current?.focus();
  }, [open, isSheet]);

  useEffect(() => {
    if (!open || isSheet) return;
    panelRef.current?.focus();
  }, [open, isSheet]);

  if (!enabled) return null;

  const count = unread?.count ?? 0;
  const label = count > 0 ? `Notifications, ${countLabel(count)} unread` : "Notifications";
  const listPending = !page && !listError;

  async function markItemRead(item: NotificationEventRow) {
    // View as is read-only. The bell is not mounted then, and this guard
    // keeps mark-read off if a view-as id is set anyway.
    if (item.in_portal_read_at || getImpersonationUserId()) return;
    try {
      await api.notifications.markRead(item.id);
      await Promise.all([mutateUnread(), mutateList()]);
    } catch {
      // The row is still in the list. A failed mark-read can be retried.
    }
  }

  async function markAll() {
    // View as is read-only. Return before the in-flight guard so a blocked
    // click does not stick the button on Marking….
    if (getImpersonationUserId()) return;
    if (markingAllRef.current) return;
    markingAllRef.current = true;
    const hadFocus = document.activeElement === markAllButtonRef.current;
    setMarkingAll(true);
    setMarkAllError(false);
    // Clear the announcement so a second success changes the live text.
    setMarkedAll(false);
    try {
      await api.notifications.markAllRead();
      await Promise.all([mutateUnread(), mutateList()]);
      setMarkedAll(true);
      // The button unmounts once the count drops, which would leave focus
      // on the body while the panel stays open.
      if (hadFocus) (isSheet ? sheetRef : panelRef).current?.focus();
    } catch {
      setMarkAllError(true);
      // The button stays mounted. If focus left it during the request, put
      // it back so the alert sits with the control that failed.
      if (hadFocus) markAllButtonRef.current?.focus();
    } finally {
      markingAllRef.current = false;
      setMarkingAll(false);
    }
  }

  function onMore(item: NotificationEventRow) {
    const opening = !expanded[item.id];
    setExpanded((current) => ({ ...current, [item.id]: opening }));
    if (opening) void markItemRead(item);
  }

  const dialogBody = (
    <>
      <div className="notif-popover-header">
        <h2 id={headingId} className="notif-popover-title">
          Notifications
        </h2>
        {count > 0 && (
          <button
            ref={markAllButtonRef}
            type="button"
            className="notif-link-btn"
            onClick={() => void markAll()}
            aria-disabled={markingAll || undefined}
          >
            {markingAll ? "Marking…" : "Mark all as read"}
          </button>
        )}
        {isSheet && (
          <button
            type="button"
            className="notif-close"
            aria-label="Close notifications"
            onClick={close}
          >
            <Icon name="x" size={20} />
          </button>
        )}
      </div>
      {markAllError && (
        <p className="notif-mark-error" role="alert">
          Couldn't mark as read. Try again.
        </p>
      )}
      <div className="notif-sr" aria-live="polite">
        {markedAll ? "All notifications marked as read" : ""}
      </div>
      <ul className="notif-popover-body" aria-busy={listPending}>
        {listError && !page ? (
          <li className="notif-state">
            <p>We couldn't load your notifications.</p>
            <button
              type="button"
              className="notif-link-btn"
              onClick={() => void mutateList()}
            >
              Try again
            </button>
          </li>
        ) : !page ? (
          <li className="notif-state">Loading notifications…</li>
        ) : page.items.length === 0 ? (
          <li className="notif-state">
            <p>You're all caught up.</p>
            <p>New notifications will show here.</p>
          </li>
        ) : (
          page.items.map((item) => (
            <NotificationRow
              isStaff={isStaff}
              key={item.id}
              item={item}
              baseId={baseId}
              expanded={Boolean(expanded[item.id])}
              onMore={onMore}
              onView={(item, href) => {
                setOpen(false);
                if (href === pathname) bellRef.current?.focus();
                void markItemRead(item);
              }}
            />
          ))
        )}
      </ul>
      {page && page.total > page.items.length && (
        <div className="notif-popover-foot">
          {`Showing the ${page.items.length} most recent of ${page.total}.`}
        </div>
      )}
    </>
  );

  return (
    <div className="notif-bell" ref={wrapRef}>
      <button
        ref={bellRef}
        type="button"
        className="notif-bell-btn"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="bell" size={20} />
        {count > 0 && (
          <span className="badge-count notif-bell-badge" aria-hidden="true">
            {countLabel(count)}
          </span>
        )}
      </button>
      {open && !isSheet && (
        <div
          ref={panelRef}
          id={panelId}
          className="notif-popover"
          role="dialog"
          aria-labelledby={headingId}
          tabIndex={-1}
        >
          {dialogBody}
        </div>
      )}
      {open &&
        isSheet &&
        createPortal(
          <>
            <div
              className="notif-sheet-backdrop"
              onClick={close}
              aria-hidden="true"
            />
            <div
              ref={sheetRef}
              id={panelId}
              className="notif-popover notif-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby={headingId}
              tabIndex={-1}
            >
              {dialogBody}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function NotificationRow({
  isStaff,
  item,
  baseId,
  expanded,
  onMore,
  onView,
}: {
  isStaff: boolean;
  item: NotificationEventRow;
  baseId: string;
  expanded: boolean;
  onMore: (item: NotificationEventRow) => void;
  onView: (item: NotificationEventRow, href: string) => void;
}) {
  const titleRef = useRef<HTMLParagraphElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [overflows, setOverflows] = useState(false);
  const unreadRow = !item.in_portal_read_at;
  const title = notificationTitle(item);
  const meta = notificationMeta(item);
  const excerpt = notificationExcerpt(item);
  const full = notificationFullText(item);
  const href = notificationHref(item, { isStaff });
  const time = notificationTime(item.created_at);
  const titleId = `${baseId}-title-${item.id}`;
  const bodyId = `${baseId}-body-${item.id}`;
  const textIsLonger = full.replace(/\s+/g, " ").trim().length > excerpt.length;
  const showText = Boolean(excerpt) || (expanded && Boolean(full));
  // Point at the body only when that element is actually rendered.
  const controlsId = showText ? bodyId : titleId;

  useEffect(() => {
    // While the row is open the clamp is off, so a fresh measure would hide
    // "Show less". Measure only the clamped layout.
    if (expanded) return;
    const nodes = [titleRef.current, bodyRef.current].filter(
      (node): node is HTMLParagraphElement => node !== null,
    );
    if (nodes.length === 0) return;

    function measure() {
      setOverflows(nodes.some((node) => isClamped(node)));
    }

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, [expanded, excerpt, title]);

  // Overflow is measured on the clamped title and excerpt. A row with no
  // destination also expands when the full text is longer than that excerpt,
  // which a short first paragraph would not overflow on its own.
  const showFull = !href && (overflows || expanded || textIsLonger);

  return (
    <li className={"notif-row" + (unreadRow ? " unread" : "") + (expanded ? " expanded" : "")}>
      {unreadRow && <span className="notif-row-dot" aria-hidden="true" />}
      {unreadRow && <span className="notif-sr">Unread, </span>}
      <p ref={titleRef} id={titleId} className="notif-row-title">
        {title}
      </p>
      <div className="notif-row-meta">
        <span className="notif-row-type">{meta.typeLabel}</span>
        {meta.chip && (
          <span className={meta.chip.className}>
            {meta.chip.dot && <span className="d" aria-hidden="true" />}
            {meta.chip.label}
          </span>
        )}
        {time.label && (
          <time className="notif-row-time" dateTime={item.created_at} title={time.title}>
            {time.label}
          </time>
        )}
      </div>
      {showText && (
        <p
          ref={bodyRef}
          id={bodyId}
          className={"notif-row-text" + (expanded ? " full" : "")}
        >
          {expanded ? full : excerpt}
        </p>
      )}
      {(href || showFull) && (
        <div className="notif-row-actions">
          {href ? (
            <Link
              to={href}
              className="notif-row-link"
              aria-label={`View: ${title}`}
              onClick={() => onView(item, href)}
            >
              View
            </Link>
          ) : (
            <button
              type="button"
              className="notif-row-more"
              aria-expanded={expanded}
              aria-controls={controlsId}
              onClick={() => onMore(item)}
            >
              {expanded ? "Show less" : "Show full notice"}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
